import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import type { HtmlViewerMessage } from "../../../shared/htmlViewer";
import { HTML_VIEWER_ROUTE } from "../../../shared/reservedRoutes";
import { publicUrl } from "../../lib/tenantOrigin";

// No allow-same-origin: the document gets an opaque origin. Read-only by
// construction — pair with LiveBrowser when the caller needs an
// authenticated, interactive iframe.
const sandbox = "allow-scripts";

type Props = {
  html: string;
  baseRoute?: string;
  title?: string;
  className?: string;
};

// An anonymous viewer for untrusted HTML: the html-viewer harness loads in
// the iframe, the parent posts it a MessagePort, and the html goes over the
// port to be document.write'n — so the content never appears in a URL and
// the harness controls its own origin. Always inert: the whole point is
// showing bytes without interaction.
export function HtmlViewer({ html, baseRoute, title, className }: Props) {
  const src = `${publicUrl()}${HTML_VIEWER_ROUTE}`;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const portRef = useRef<MessagePort | null>(null);
  const htmlRef = useRef(html);
  htmlRef.current = html;
  const baseRouteRef = useRef(baseRoute);
  baseRouteRef.current = baseRoute;
  const pendingRenderRef = useRef(false);

  // Assigning src (even the same string) reloads the shell, so every html
  // or src change funnels through one load path. document.write in the
  // shell fires a second `load`; pendingRenderRef gates the spurious fire
  // so it doesn't close the port under a healthy render.
  useLayoutEffect(() => {
    pendingRenderRef.current = true;
    if (iframeRef.current) iframeRef.current.src = src;
  }, [html, src]);

  const onLoad = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    if (!pendingRenderRef.current) return;
    pendingRenderRef.current = false;

    portRef.current?.close();
    const channel = new MessageChannel();
    portRef.current = channel.port1;
    iframeWindow.postMessage({ type: "init" }, "*", [channel.port2]);

    const renderMessage: HtmlViewerMessage = {
      type: "render",
      data: htmlRef.current,
      baseRoute: baseRouteRef.current,
    };
    channel.port1.postMessage(renderMessage);
  }, []);

  useEffect(
    () => () => {
      portRef.current?.close();
    },
    []
  );

  return (
    <iframe
      ref={iframeRef}
      sandbox={sandbox}
      onLoad={onLoad}
      inert
      title={title}
      className={className}
    />
  );
}
